import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'

import {CodeViewBlameEntrypoint} from '../pages/CodeViewBlameEntrypoint'
import {CodeViewBlobEntrypoint} from '../pages/CodeViewBlobEntrypoint'
import {CodeViewBlobLayout} from '../pages/CodeViewBlobLayout'
import {CodeViewBlobLayoutErrorBoundary} from '../pages/CodeViewBlobLayoutErrorBoundary'
import {CodeViewDeleteEntrypoint} from '../pages/CodeViewDeleteEntrypoint'
import {CodeViewEditEntrypoint} from '../pages/CodeViewEditEntrypoint'
import {CodeViewFileTreeLayout} from '../pages/CodeViewFileTreeLayout'
import {CodeViewLayout} from '../pages/CodeViewLayout'
import {CodeViewNewEntrypoint} from '../pages/CodeViewNewEntrypoint'
import {CodeViewRepoEntrypoint} from '../pages/CodeViewRepoEntrypoint'
import {CodeViewTreeDeleteEntrypoint} from '../pages/CodeViewTreeDeleteEntrypoint'
import {CodeViewTreeEntrypoint} from '../pages/CodeViewTreeEntrypoint'
import {CodeViewTreeErrorBoundary} from '../pages/CodeViewTreeErrorBoundary'
import {buildCodeViewBlameRoute} from './code-view-blame-route'
import {buildCodeViewBlobLayoutRoute} from './code-view-blob-layout-route'
import {buildCodeViewBlobRoute} from './code-view-blob-route'
import {buildCodeViewDeleteRoute} from './code-view-delete-route'
import {buildCodeViewEditRoute} from './code-view-edit-route'
import {buildCodeViewFileTreeLayoutRoute} from './code-view-file-tree-layout-route'
import {buildCodeViewLayoutRoute} from './code-view-layout-route'
import {buildCodeViewNewRoute} from './code-view-new-route'
import {buildCodeViewRepoRoute} from './code-view-repo-route'
import {buildCodeViewTreeDeleteRoute} from './code-view-tree-delete-route'
import {buildCodeViewTreeRoute} from './code-view-tree-route'

/**
 * Builds the Code View route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `code-view`-bound singletons directly.
 *
 * Note: the `tanStackRouterEnabled` option is set at the app level (on
 * `createDataRouterAppFromRoutes`), not here, so a consuming app opts into
 * TanStack Router independently of these route definitions.
 */
export function createCodeViewRoutes(appBuilder: DataRouterApplicationBuilder<string>): RouteObject[] {
  return [
    buildCodeViewLayoutRoute(appBuilder).toRoute({
      Component: CodeViewLayout,
      children: [
        buildCodeViewRepoRoute(appBuilder).toRoute({
          Component: CodeViewRepoEntrypoint,
        }),
        buildCodeViewFileTreeLayoutRoute(appBuilder).toRoute({
          Component: CodeViewFileTreeLayout,
          children: [
            // Most specific routes first (more path segments)
            buildCodeViewTreeDeleteRoute(appBuilder).toRoute({
              Component: CodeViewTreeDeleteEntrypoint,
            }),
            buildCodeViewEditRoute(appBuilder).toRoute({
              Component: CodeViewEditEntrypoint,
            }),
            buildCodeViewDeleteRoute(appBuilder).toRoute({
              Component: CodeViewDeleteEntrypoint,
            }),
            // Routes without :path parameter
            buildCodeViewNewRoute(appBuilder).toRoute({
              Component: CodeViewNewEntrypoint,
            }),
            buildCodeViewTreeRoute(appBuilder).toRoute({
              Component: CodeViewTreeEntrypoint,
              ErrorBoundary: CodeViewTreeErrorBoundary,
            }),
            buildCodeViewBlobLayoutRoute(appBuilder).toRoute({
              Component: CodeViewBlobLayout,
              ErrorBoundary: CodeViewBlobLayoutErrorBoundary,
              children: [
                buildCodeViewBlobRoute(appBuilder).toRoute({
                  Component: CodeViewBlobEntrypoint,
                }),
                buildCodeViewBlameRoute(appBuilder).toRoute({
                  Component: CodeViewBlameEntrypoint,
                }),
              ],
            }),
          ],
        }),
      ],
    }),
  ]
}
